/**
 * Web Vitals monitoring utilities
 * Tracks Core Web Vitals (CWV) metrics for performance monitoring
 */

import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';

export interface WebVitalsReport {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
  navigationType: string;
}

/**
 * Thresholds for Core Web Vitals based on web.dev recommendations
 */
const THRESHOLDS = {
  CLS: { good: 0.1, poor: 0.25 },
  FCP: { good: 1800, poor: 3000 },
  INP: { good: 200, poor: 500 },
  LCP: { good: 2500, poor: 4000 },
  TTFB: { good: 800, poor: 1800 },
};

/**
 * Determine the rating for a metric based on its value
 */
function getRating(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const threshold = THRESHOLDS[name as keyof typeof THRESHOLDS];
  if (!threshold) return 'good';
  
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

/**
 * Format a metric for reporting
 */
function formatMetric(metric: Metric): WebVitalsReport {
  return {
    name: metric.name,
    value: metric.value,
    rating: getRating(metric.name, metric.value),
    delta: metric.delta,
    id: metric.id,
    navigationType: metric.navigationType,
  };
}

/**
 * Send metrics to analytics endpoint (optional)
 * Can be configured to send to Google Analytics, custom endpoint, etc.
 */
function sendToAnalytics(metric: WebVitalsReport): void {
  // Log to console in development
  if (import.meta.env.DEV) {
    console.log('[Web Vitals]', metric);
  }

  // In production, you could send to analytics:
  // - Google Analytics 4
  // - Custom analytics endpoint
  // - Performance monitoring service
  
  // Example: Send to custom endpoint
  // if (import.meta.env.PROD) {
  //   const body = JSON.stringify(metric);
  //   const url = '/api/analytics';
  //   
  //   // Use `navigator.sendBeacon()` if available, falling back to `fetch()`
  //   if (navigator.sendBeacon) {
  //     navigator.sendBeacon(url, body);
  //   } else {
  //     fetch(url, { body, method: 'POST', keepalive: true });
  //   }
  // }
}

/**
 * Initialize Web Vitals monitoring
 * Call this once when the app starts
 */
export function initWebVitals(): void {
  // Cumulative Layout Shift (CLS)
  // Measures visual stability
  onCLS((metric) => {
    sendToAnalytics(formatMetric(metric));
  });

  // First Contentful Paint (FCP)
  // Measures time to first content render
  onFCP((metric) => {
    sendToAnalytics(formatMetric(metric));
  });

  // Interaction to Next Paint (INP)
  // Measures overall responsiveness
  onINP((metric) => {
    sendToAnalytics(formatMetric(metric));
  });

  // Largest Contentful Paint (LCP)
  // Measures loading performance
  onLCP((metric) => {
    sendToAnalytics(formatMetric(metric));
  });

  // Time to First Byte (TTFB)
  // Measures server response time
  onTTFB((metric) => {
    sendToAnalytics(formatMetric(metric));
  });
}

/**
 * Get current Web Vitals metrics (for debugging/display)
 */
export async function getCurrentMetrics(): Promise<WebVitalsReport[]> {
  const metrics: WebVitalsReport[] = [];

  return new Promise((resolve) => {
    // Collect all metrics
    const collector = (metric: Metric) => {
      metrics.push(formatMetric(metric));
    };

    onCLS(collector);
    onFCP(collector);
    onINP(collector);
    onLCP(collector);
    onTTFB(collector);

    // Wait a bit for metrics to be collected
    setTimeout(() => resolve(metrics), 100);
  });
}

/**
 * Performance budget checker
 * Returns true if all metrics meet the "good" threshold
 */
export async function checkPerformanceBudget(): Promise<{
  passed: boolean;
  metrics: WebVitalsReport[];
  failures: string[];
}> {
  const metrics = await getCurrentMetrics();
  const failures = metrics
    .filter((m) => m.rating === 'poor')
    .map((m) => `${m.name}: ${m.value.toFixed(2)} (threshold: ${THRESHOLDS[m.name as keyof typeof THRESHOLDS]?.good})`);

  return {
    passed: failures.length === 0,
    metrics,
    failures,
  };
}
